import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { FileTypeEnum } from '@repo/toolkit/types'
import UploadFileInput from '@repo/ui/file-input'
import { UploadIcon } from '@repo/ui/icons'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import useTranslate from '../../../../lib/hooks/use-translate'
import { QuotationInput } from '../../../../lib/types/models/quotation/quotation-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ValidationButtons from '../../../elements/validation-buttons'
import { QUOTATION_SCHEMA_VALIDATION } from '../utils'

type Props = BaseFormProps<QuotationInput> & {
  className?: string
}

const QuotationForm = ({ defaultValues, className, ...validationButtonProps }: Props) => {
  const { currentLanguage } = useTranslate()
  const form = useForm<QuotationInput>({
    resolver: zodResolver(QUOTATION_SCHEMA_VALIDATION(currentLanguage)),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={'divide-y divide-white-active'}>
          <div className={cn('flex flex-col gap-3', className)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput required {...field} label={'Document name'} type={'text'} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="validUntil"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledDateStringInput
                      {...field}
                      required
                      mode="over"
                      label={'Valid until'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'file'}
              render={({ field, fieldState }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <UploadFileInput
                      required
                      placeholder="Drag and drop or browse"
                      types={[FileTypeEnum.IMAGE, FileTypeEnum.PDF]}
                      icon={UploadIcon}
                      hasError={!!fieldState.error}
                      label={'Upload document'}
                      files={field?.value ? [field?.value] : undefined}
                      onFilesChange={(files) => {
                        field.onChange(files.length === 0 ? null : files[0])
                      }}
                      onFileError={(error) => {
                        if (error) toast.error(error)
                      }}
                      previewOnClick
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="pb-6 px-4">
            <ValidationButtons {...validationButtonProps} />
          </div>
        </div>
      </form>
    </Form>
  )
}

export default QuotationForm
